import axios from "axios";
import { getAuthHeaders } from "../firebase/authHeaders";
import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import { toast } from "react-toastify";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ProfileButton from "../components/commons/ProfileButton";
import TextInput from "../components/commons/TextInput";
import PhoneInput from "../components/commons/PhoneInput";
import DialogLoading from "../components/dialogs/DialogLoading";
import DialogMembership from "../components/dialogs/DialogMembership";
import DialogConfirmation from "../components/dialogs/DialogConfirmation";
import ProfileTabs from "../components/ProfileTabs";
import ProfileTitle from "../components/ProfileTitle";
import UploadInput from "../components/commons/UploadInput"
import { useAuth } from "../context/authContext";
import { createCheckouts } from "../firebase/payments";
import { getUserData, updateUserProfile, checkPhoneNumberExists, saveShippingInfo, getShippingInfo } from "../firebase/users";
import { toE164Format, formatForDisplay, validatePhoneNumber } from "../utils/phoneUtils";

import { 
  PhoneAuthProvider, 
  signInWithCredential, 
  linkWithCredential,
  RecaptchaVerifier 
} from "firebase/auth";
import PlanDialog from "../components/dialogs/PlanDialog";
import { auth } from "../firebase/config";

const UserProfile = () => {
  const { user, getUpdatedUserData, membershipsForUser } = useAuth();
  const [initialData, setUserInitialData] = useState();
  const [loading, setLoading] = useState(true);
  const [showDialogMemebership, setShowDialogMembership] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [activeDialogPlan, setActiveDialogPlan] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const planLabels = {
    basic: "Basic",
    pro: "Pro",
    elite: "Elite",
  };

  const openPlanDialog = (plan) => {
    setActiveDialogPlan(plan);
    setShowPlanDialog(true);
  };

  const closePlanDialog = () => {
    setShowPlanDialog(false);
    setActiveDialogPlan(null);
  };

  const annualSavingsText = (plan) => {
    const monthly = membershipsForUser[plan]?.price;
    const yearly = membershipsForUser[`${plan}Yearly`]?.price;
    if (!monthly || !yearly) return "Annual billing available.";
    const savings = Math.round((1 - yearly / (monthly * 12)) * 100);
    return savings > 0
      ? `Save ${savings}% with annual billing.`
      : "Annual billing available.";
  };

  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [imageUpload, setImageUpload] = useState(null);
  const [editTimes, setEditTimes] = useState(0);
  const [image, setImage] = useState(null);
  const [showSmsVerification, setShowSmsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState("");
  const [phoneAuthProvider, setPhoneAuthProvider] = useState(null);
  const [phoneNumberFormatted, setPhoneNumberFormatted] = useState(false);
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState("");
  const [userInputPhoneNumber, setUserInputPhoneNumber] = useState("");
  const [checkingPhoneNumber, setCheckingPhoneNumber] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    phone: ''
  });
  const [shippingFormData, setShippingFormData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    phone: ''
  });
  const recaptchaVerifierRef = useRef(null);

  const sliderSettings = {
    dots: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    navigator: false,
    customPaging: function (i) {
      return (
        <div className="slick-dot lg:hidden w-[8px] lg:w-[10px] h-[8px] lg:h-[10px] cursor-pointer bg-white/30 backdrop-blur-sm"></div>
      );
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const [userData, setUserData] = useState({});
  const [formData, setFormData] = useState({});
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (user) {
      // Reset the loaded flag when user changes
      dataLoadedRef.current = false;
      getUserData(user.uid).then((data) => {
        console.log('UserProfile - Loaded user data:', data); // Debug log
        console.log('UserProfile - Phone number from DB:', data?.phoneNumber); // Debug phone number
        console.log('UserProfile - Phone number type:', typeof data?.phoneNumber); // Debug phone number type
        console.log('UserProfile - Phone number length:', data?.phoneNumber?.toString().length); // Debug phone number length
        console.log('UserProfile - All data keys:', Object.keys(data || {})); // Debug all keys
        
        if (!data) {
          console.error('UserProfile - getUserData returned null/undefined');
          return;
        }
        
        // Only set data if we haven't loaded it yet
        if (!dataLoadedRef.current) {
          console.log('UserProfile - First time loading data, setting states');
          setUserData(data);
          setFormData(data);
          setUserInitialData(data);
          dataLoadedRef.current = true;
        } else {
          console.log('UserProfile - Data already loaded, skipping state update');
        }

        // Load shipping information
        loadShippingInfo();

        setTimeout(() => {
          setLoading(false);
        }, 200);
      });
    }
  }, [user]);

  const loadShippingInfo = async () => {
    try {
      const savedShippingInfo = await getShippingInfo(user.uid);
      console.log('UserProfile - Loaded shipping info:', savedShippingInfo);
      console.log('UserProfile - Shipping phone:', savedShippingInfo?.phone);
      
      if (savedShippingInfo) {
        setShippingInfo(savedShippingInfo);
        // Initialize shipping form data with saved shipping info
        setShippingFormData(prev => {
          console.log('UserProfile - Setting shipping form data:', savedShippingInfo);
          return !prev.name ? savedShippingInfo : prev;
        });
      }
    } catch (error) {
      console.error('Error loading shipping info:', error);
    }
  };

  // Cleanup reCAPTCHA on component unmount
  useEffect(() => {
    return () => {
      try {
        if (recaptchaVerifierRef.current && typeof recaptchaVerifierRef.current.clear === 'function') {
          recaptchaVerifierRef.current.clear();
        }
      } catch (error) {
        console.log('reCAPTCHA already cleared or destroyed');
      }
      recaptchaVerifierRef.current = null;
      
      // Remove the container from DOM with delay
      setTimeout(() => {
        const container = document.getElementById('recaptcha-container');
        if (container && container.parentNode) {
          try {
            container.parentNode.removeChild(container);
          } catch (error) {
            console.log('Container already removed');
          }
        }
      }, 100);
      
      sessionStorage.removeItem('phoneVerificationId');
    };
  }, []);

  const formatPhoneNumber = (phoneNumber) => {
    console.log('UserProfile - Formatting phone number:', phoneNumber); // Debug log
    
    if (!phoneNumber || typeof phoneNumber !== 'string') {
      console.log('UserProfile - Phone number is null or not string'); // Debug log
      return null;
    }
    
    // Remove all non-digit characters except +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // If it already starts with +, validate the format
    if (cleaned.startsWith('+')) {
      // Check if it's a valid international format
      const internationalRegex = /^\+[1-9]\d{1,14}$/;
      if (internationalRegex.test(cleaned)) {
        return cleaned; // Already in correct format
      } else {
        return null; // Invalid international format
      }
    }
    
    // Handle different length scenarios
    if (!cleaned.startsWith('+')) {
      if (cleaned.length === 9) {
        // For 9-digit numbers, add +1 and a leading 0 to make it valid
        // This converts 123456789 to +10123456789 (valid format)
        cleaned = '+1' + '0' + cleaned;
      } else if (cleaned.length === 10) {
        // If it's a 10-digit number, assume US (+1)
        cleaned = '+1' + cleaned;
      } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // If it's 11 digits starting with 1, add +
        cleaned = '+' + cleaned;
      } else if (cleaned.length < 9) {
        // For very short numbers, pad with zeros to make them valid
        const padded = cleaned.padEnd(9, '0');
        cleaned = '+1' + '0' + padded;
      } else {
        // For other cases, add +1 as default
        cleaned = '+1' + cleaned;
      }
    }
    
    console.log('UserProfile - Final formatted phone number:', cleaned); // Debug log
    return cleaned;
  };

  const validatePhoneNumber = (phoneNumber) => {
    console.log('UserProfile - Validating phone number:', phoneNumber); // Debug log
    
    const formatted = formatPhoneNumber(phoneNumber);
    console.log('UserProfile - Formatted phone number:', formatted); // Debug log
    
    if (!formatted) {
      console.log('UserProfile - Phone number formatting failed'); // Debug log
      return { 
        isValid: false, 
        formatted: null, 
        error: "Invalid phone number format. Please use international format (e.g., +1234567890) or local format (e.g., 123-456-7890)" 
      };
    }
    
    // Basic validation for international phone number (allow 10-15 digits total)
    const phoneRegex = /^\+[1-9]\d{9,14}$/;
    const isValid = phoneRegex.test(formatted);
    console.log('UserProfile - Phone number validation result:', isValid); // Debug log
    
    if (!isValid) {
      return { 
        isValid: false, 
        formatted: null, 
        error: "Invalid phone number format" 
      };
    }
    
    return { isValid: true, formatted, error: null };
  };



  const onSubmitUserProfile = async () => {
    // Combine form data with user data
    const combinedData = { ...userData, ...formData };
    
    console.log('Form submission - userData:', userData);
    console.log('Form submission - formData:', formData);
    console.log('Form submission - combinedData:', combinedData);
    
    // Check if phone number has changed
    if (combinedData.phoneNumber && combinedData.phoneNumber !== initialData.phoneNumber) {
      // Validate and format phone number
      const phoneValidation = validatePhoneNumber(combinedData.phoneNumber);
      
      if (!phoneValidation.isValid) {
        toast.error(phoneValidation.error + ". Please use international format (e.g., +1234567890)");
        return;
      }
      
      // Check if phone number is already in use by another user
      setCheckingPhoneNumber(true);
      let phoneNumberAvailable = true;
      
      try {
        const phoneCheck = await checkPhoneNumberExists(phoneValidation.formatted, user.uid);
        console.log('Phone check result:', phoneCheck); // Debug log
        if (phoneCheck.exists) {
          toast.error("This phone number is already registered by another user. Please use a different phone number.");
          phoneNumberAvailable = false;
        }
      } catch (error) {
        console.error('Error checking phone number:', error);
        toast.error("Error checking phone number availability. Please try again.");
        phoneNumberAvailable = false;
      } finally {
        setCheckingPhoneNumber(false);
      }
      
      // If phone number is not available, stop here
      if (!phoneNumberAvailable) {
        return;
      }
      

      
      // Track if phone number was formatted (only show notification for local format conversion)
      const wasFormatted = phoneValidation.formatted !== combinedData.phoneNumber && !combinedData.phoneNumber.startsWith('+');
      if (wasFormatted) {
        setPhoneNumberFormatted(true);
        setOriginalPhoneNumber(combinedData.phoneNumber);
      }
      
      // Store the original user input for display purposes
      const originalUserInput = combinedData.phoneNumber;
      setUserInputPhoneNumber(originalUserInput);
      
      // Update the form data with formatted phone number for database storage
      setFormData(prev => ({ ...prev, phoneNumber: phoneValidation.formatted }));
      
      console.log('SMS verification opening - formData after update:', formData);
      console.log('SMS verification opening - userData:', userData);
      
      // Phone number has changed, trigger Firebase Phone Auth
      // Store the original user input for display, but use formatted for verification
      setPendingPhoneNumber(originalUserInput);
      await sendSmsVerification(phoneValidation.formatted);
      setShowSmsVerification(true);
    } else {
      // No phone number change, update profile and shipping info directly
      try {
        await updateUserProfile(user.uid, combinedData);
        
        // Save shipping information
        await saveShippingInfo(user.uid, shippingFormData);
        
        getUpdatedUserData();
        toast.success("Profile and shipping information successfully updated!");
      } catch (error) {
        console.error('Error updating profile:', error);
        toast.error("Error updating profile. Please try again.");
      }
    }
  };

  const handleMembership = async (newMembership) => {
    setLoading(true);

    axios({
      method: "post",
      url: process.env.REACT_APP_PAYMENT_API + "/create-membership-checkout",
      headers: await getAuthHeaders(),
      data: {
        priceId: membershipsForUser[newMembership].priceId,
        prevLink: window.location.href,
        customerId: initialData.customer_id,
      },
    }).then(async ({ data }) => {
      await createCheckouts({
        sessionId: data.session.id,
        sessionLink: data.session.url,
        checkoutInfo: {
          type: "membership",
          plan: newMembership,
          user_id: initialData.id,
          price: membershipsForUser[newMembership].price,
        },
      });
      window.location.href = data.session.url;

      setTimeout(() => {
        setLoading(false);
      }, 200);
    }).catch((error) => {
      console.error('Error creating checkout session:', error);
      toast.error(error.message||"Error initiating membership upgrade. Please try again.");
    })
  };

  // const handleUpload = async () => {
  //   if (!imageUpload) return;
  
  //   const imageRef = ref(storage, `profileImages/${user.uid}_${imageUpload.name}`);
  //   await uploadBytes(imageRef, imageUpload);
  //   const downloadURL = await getDownloadURL(imageRef);
  
  //   userData["photoURL"] = downloadURL;
  //   setProfileImageUrl(downloadURL);
  //   toast.success("Profile picture uploaded!");
  // };

  const onEditImage = () => {
    setEditTimes(editTimes + 1);
    // Reset the image state to allow new uploads
    setImage(null);
  }

  const initializeRecaptcha = async () => {
    try {
      // Clear any existing reCAPTCHA
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          console.log('reCAPTCHA already cleared');
        }
        recaptchaVerifierRef.current = null;
      }
      
      // Create fresh reCAPTCHA container
      await createRecaptchaContainer();
      
      // Ensure container exists before creating verifier
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        throw new Error('reCAPTCHA container not found');
      }
      
      // Create verifier with better error handling
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
        'callback': (response) => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          recaptchaVerifierRef.current = null;
        },
        'error-callback': (error) => {
          console.error('reCAPTCHA error:', error);
          recaptchaVerifierRef.current = null;
        }
      });
      
      // Log that we're using reCAPTCHA v2 (which is expected for Phone Auth)
      console.log('reCAPTCHA v2 initialized for Phone Authentication');
      recaptchaVerifierRef.current = verifier;
      
      // Wait for reCAPTCHA to initialize
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return true;
    } catch (error) {
      console.error("Error initializing reCAPTCHA:", error);
      return false;
    }
  };

  const handlePhoneVerification = async (phoneNumber) => {
    // Validate the phone number first
    const validation = validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    // Convert to E.164 format for Firebase
    const e164PhoneNumber = toE164Format(phoneNumber);
    
    // Check if phone number is already in use by another user
    setCheckingPhoneNumber(true);
    try {
      const phoneCheck = await checkPhoneNumberExists(e164PhoneNumber, user.uid);
      if (phoneCheck.exists) {
        toast.error("This phone number is already registered by another user. Please use a different phone number.");
        return;
      }
    } catch (error) {
      console.error('Error checking phone number:', error);
      toast.error("Error checking phone number availability. Please try again.");
      return;
    } finally {
      setCheckingPhoneNumber(false);
    }

    // Store the E.164 formatted number for verification
    setPendingPhoneNumber(e164PhoneNumber);
    
    // Send SMS verification
    await sendSmsVerification(e164PhoneNumber);
  };

  const sendSmsVerification = async (phoneNumber) => {
    try {
      setLoading(true);
      
      console.log('sendSmsVerification - Phone number:', phoneNumber); // Debug log
      console.log('sendSmsVerification - NODE_ENV:', process.env.NODE_ENV); // Debug log
      
      // Comment out development mode check to always send real SMS
      // if (process.env.NODE_ENV === 'development') {
      //   // For development/testing, simulate the verification process
      //   console.log('Development mode: Simulating SMS verification');
      //   
      //   // Simulate a delay
      //   await new Promise(resolve => setTimeout(resolve, 1000));
      //   
      //   // Store a mock verification ID
      //   sessionStorage.setItem('phoneVerificationId', 'mock-verification-id');
      //   
      //   toast.success("Verification code sent to your phone number! (Development mode)");
      //   return;
      // }
      
      // Initialize reCAPTCHA (with fallback for development)
      let recaptchaReady = false;
      if (process.env.NODE_ENV === 'development') {
        // In development, skip reCAPTCHA to avoid issues
        console.log('Development mode: Skipping reCAPTCHA initialization');
        recaptchaReady = true;
      } else {
        recaptchaReady = await initializeRecaptcha();
        if (!recaptchaReady) {
          toast.error("Failed to initialize verification. Please refresh and try again.");
          return;
        }
      }
      
      // Create phone auth provider
      const provider = new PhoneAuthProvider(auth);
      setPhoneAuthProvider(provider);
      
      // Send verification code
      let verificationId;
      if (process.env.NODE_ENV === 'development') {
        // In development, simulate verification
        console.log('Development mode: Simulating SMS verification');
        await new Promise(resolve => setTimeout(resolve, 1000));
        verificationId = 'mock-verification-id';
      } else {
        verificationId = await provider.verifyPhoneNumber(
          phoneNumber, 
          recaptchaVerifierRef.current
        );
      }
      
      // Store verification ID in session storage for verification
      sessionStorage.setItem('phoneVerificationId', verificationId);
      
      toast.success("Verification code sent to your phone number!");
    } catch (error) {
      console.error("Error sending SMS verification:", error);
      console.error("Error code:", error.code); // Debug log
      console.error("Error message:", error.message); // Debug log
      
      if (error.code === 'auth/invalid-phone-number') {
        toast.error("Invalid phone number format. Please use international format (e.g., +1234567890)");
      } else if (error.code === 'auth/too-many-requests') {
        toast.error("Too many verification attempts. Please try again later.");
      } else if (error.code === 'auth/quota-exceeded') {
        toast.error("SMS quota exceeded. Please try again later.");
      } else if (error.code === 'auth/operation-not-allowed') {
        toast.error("Phone authentication is not enabled. Please contact support.");
      } else if (error.code === 'auth/invalid-app-credential') {
        toast.error("Firebase Phone Authentication is not configured. Please configure it in Firebase Console.");
      } else {
        toast.error("Failed to send verification code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifySmsCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error("Please enter a valid 6-digit verification code");
      return;
    }

    try {
      setIsVerifying(true);
      
      // Get verification ID from session storage
      const verificationId = sessionStorage.getItem('phoneVerificationId');
      if (!verificationId) {
        toast.error("Verification session expired. Please try again.");
        return;
      }
      
      // Comment out development mode verification check
      // if (verificationId === 'mock-verification-id') {
      //   // For development, accept any 6-digit code
      //   if (verificationCode === '123456') {
      //     // Verification successful, update profile and shipping info
      //     const combinedData = { ...userData, ...formData };
      //     await updateUserProfile(user.uid, combinedData);
      //     await saveShippingInfo(user.uid, shippingFormData);
      //     getUpdatedUserData();
      //     toast.success("Phone number verified and profile updated successfully! (Development mode)");
      //     setShowSmsVerification(false);
      //     setVerificationCode("");
      //     setPendingPhoneNumber("");
      //     
      //     // Clear session storage
      //     sessionStorage.removeItem('phoneVerificationId');
      //     return;
      //   } else {
      //     toast.error("Invalid verification code. Please try again.");
      //     return;
      //   }
      // }
      
      // Create credential with verification code
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      
      // Link phone credential to current user
      await linkWithCredential(user, credential);
      
      // Verification successful, update profile and shipping info
      const combinedData = { ...userData, ...formData };
      await updateUserProfile(user.uid, combinedData);
      await saveShippingInfo(user.uid, shippingFormData);
      getUpdatedUserData();
      toast.success("Phone number verified and profile updated successfully!");
      setShowSmsVerification(false);
      setVerificationCode("");
      setPendingPhoneNumber("");
      
      // Clear session storage
      sessionStorage.removeItem('phoneVerificationId');
    } catch (error) {
      console.error("Error verifying SMS code:", error);
      if (error.code === 'auth/invalid-verification-code') {
        toast.error("Invalid verification code. Please try again.");
      } else if (error.code === 'auth/code-expired') {
        toast.error("Verification code expired. Please request a new one.");
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCloseSmsVerification = () => {
    setShowSmsVerification(false);
    setVerificationCode("");
    setPendingPhoneNumber("");
    setPhoneNumberFormatted(false);
    setOriginalPhoneNumber("");
    setCheckingPhoneNumber(false);
    // Reset phone number to user's original input (not the formatted database value)
    setFormData(prev => ({ ...prev, phoneNumber: userInputPhoneNumber || initialData.phoneNumber }));
    // Clear session storage
    sessionStorage.removeItem('phoneVerificationId');
    // Clear reCAPTCHA safely
    try {
      if (recaptchaVerifierRef.current && typeof recaptchaVerifierRef.current.clear === 'function') {
        recaptchaVerifierRef.current.clear();
      }
    } catch (error) {
      console.log('reCAPTCHA already cleared or destroyed');
    }
    recaptchaVerifierRef.current = null;
    
    // Remove the container from DOM with delay to avoid conflicts
    setTimeout(() => {
      const container = document.getElementById('recaptcha-container');
      if (container && container.parentNode) {
        try {
          container.parentNode.removeChild(container);
        } catch (error) {
          console.log('Container already removed');
        }
      }
    }, 100);
  };

  const createRecaptchaContainer = () => {
    // Remove any existing container
    const existingContainer = document.getElementById('recaptcha-container');
    if (existingContainer && existingContainer.parentNode) {
      try {
        existingContainer.parentNode.removeChild(existingContainer);
      } catch (error) {
        console.log('Container already removed');
      }
    }
    
    // Wait a bit to ensure DOM is clean
    return new Promise((resolve) => {
      setTimeout(() => {
        // Create new container with better positioning
        const container = document.createElement('div');
        container.id = 'recaptcha-container';
        container.style.position = 'absolute';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '1px';
        container.style.height = '1px';
        container.style.overflow = 'hidden';
        container.style.opacity = '0';
        container.style.pointerEvents = 'none';
        document.body.appendChild(container);
        
        resolve(container);
      }, 100);
    });
  };

  const handleResendCode = async () => {
    if (pendingPhoneNumber) {
      // Clear previous verification session
      sessionStorage.removeItem('phoneVerificationId');
      await sendSmsVerification(pendingPhoneNumber);
    }
  };
  
  return (
    <>
      {loading && (
        <DialogLoading
          open={true}
          onClose={() => {}}
          type="loading"
          title="Please wait"
          message="We are processing your booking request. Please wait and don’t close this page"
          buttonText="Cancel"
          onButtonClick={() => {}}
        />
      )}

      {!loading && (
        <div>
          <ProfileTitle />

          <DialogMembership
            open={showDialogMemebership}
            onClose={() => setShowDialogMembership(false)}
          />

          <PlanDialog
            open={showPlanDialog}
            onClose={closePlanDialog}
            activePlan={activeDialogPlan}
            membershipsForUser={membershipsForUser}
            onConfirm={(membershipKey) => {
              handleMembership(membershipKey)
              closePlanDialog()
            }}
          />

          <DialogConfirmation
            open={showSmsVerification}
            onClose={handleCloseSmsVerification}
            onConfirm={verifySmsCode}
            title="SMS Verification"
            message={
              <div className="text-center">
                {phoneNumberFormatted && (
                  <div className="bg-primary/20 border border-primary/30 rounded-lg p-3 mb-4">
                    <p className="text-primary text-sm">
                      <strong>Phone number formatted:</strong><br />
                      {originalPhoneNumber} → {pendingPhoneNumber}
                    </p>
                  </div>
                )}
                

                
                <p className="text-white mb-4">
                  We've sent a verification code to <strong>{pendingPhoneNumber}</strong>
                </p>
                <div className="mb-4">
                  <TextInput
                    placeholder="Enter verification code"
                    value={verificationCode}
                    onChange={setVerificationCode}
                    inputClassName="text-center text-lg font-bold placeholder:text-white/60"
                    maxLength={6}
                  />
                </div>
                <p className="text-white/60 text-sm mb-4">
                  Please enter the 6-digit code sent to your phone number
                </p>
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-primary text-sm underline hover:text-white transition"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Resend Code"}
                </button>
              </div>
            }
            confirmText={isVerifying ? "Verifying..." : "Verify"}
            cancelText="Cancel"
            loading={isVerifying}
          />

          <ProfileTabs activeTab="Profile/Membership" />

          <div className="lg:flex justify-between gap-6 relative z-[10]">
            <span className="block uppercase text-primary text-[12px] max-lg:mb-8">
              Personal Info
            </span>
          </div>
          <div className="flex gap-6">
            <div className="flex gap-6 lg:mt-8">
              <UploadInput
                key={editTimes}
                // onFileSelect={(value) => setImage(value)}
                onFileSelect={(value) => setFormData(prev => ({ ...prev, picture: value }))}
                className="custom-upload-class !h-[140px] !w-[140px] lg:!h-[170px] lg:!w-[170px] "
                titleClassName="hidden"
                subtitleClassName="hidden"
                iconClassName="max-w-[28px] lg:max-w-[28px]"
                uploadId="category-new-image"
                editTimes={editTimes}
                defaultPreview={formData.picture || userData.picture || null}
              />

              <div>
                <div>
                  <h4 className="text-base lg:text-[24px] font-aero text-white uppercase">
                    Upload Photo
                  </h4>
                  <span className="text-white/60 block leading-[1.4] text-[10px] mt-1">
                    At least 256 x 256, PNG or JPG file <br /> format. Max
                    20 MB
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:w-[830px] ml-auto relative z-[1]">
              <div className="flex flex-col lg:flex-row gap-5 w-full mt-5 lg:mt-8">
                <label className="w-full  text-white">
                  <span className="text-sm mb-3 block">Display Name</span>
                  <TextInput
                    placeholder="Enter your name"
                    inputClassName="placeholder:text-white/60"
                    onChange={(value) => setFormData(prev => ({ ...prev, displayName: value }))}
                    value={formData.displayName || userData.displayName || ''}
                  />
                </label>
                <label className="w-full text-white">
                  <span className="text-sm mb-3 block">User ID</span>
                  <TextInput
                    placeholder="Enter your name"
                    inputClassName="placeholder:text-white/60"
                    disabled
                    value={formData.username || userData.username || ''}
                  />
                </label>
              </div>

              <div className="flex flex-col lg:flex-row gap-5 w-full mt-5 lg:mt-8">
                <label className="w-full  text-white">
                  <span className="text-sm mb-3 block">Email</span>
                  {user && user.email && (
                    <TextInput
                      placeholder="Enter your email"
                      inputClassName="placeholder:text-white/60"
                      disabled
                      defaultValue={user.email}
                    />
                  )}
                </label>
                {(() => {
                  // Handle phone number display
                  const dbPhoneNumber = userData.phoneNumber || '';
                  const editingPhoneNumber = formData.phoneNumber;
                  
                  let displayPhoneNumber = '';
                  if (editingPhoneNumber !== undefined) {
                    // User is editing - show exactly what they typed (no formatting for partial input)
                    displayPhoneNumber = editingPhoneNumber || '';
                  } else {
                    // Loading from DB - format the database value
                    displayPhoneNumber = dbPhoneNumber ? formatForDisplay(dbPhoneNumber) : '';
                  }
                  
                  console.log('UserProfile - DB phone number:', dbPhoneNumber);
                  console.log('UserProfile - Editing phone number:', editingPhoneNumber);
                  console.log('UserProfile - Display phone number:', displayPhoneNumber);
                  
                  return (
                    <PhoneInput
                      placeholder="+1234567890 or 123-456-7890"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => {
                                console.log('PhoneInput onChange - value:', value, 'type:', typeof value);
                                setFormData(prev => ({ ...prev, phoneNumber: value }));
                      }}
                      defaultValue={String(displayPhoneNumber || '')}
                      disabled={checkingPhoneNumber}
                      showVerifyButton={true}
                      isVerifying={isVerifying}
                      onVerify={handlePhoneVerification}
                      label={
                        <span>
                          Phone number
                          {checkingPhoneNumber && (
                            <span className="ml-2 text-primary text-xs">(Checking availability...)</span>
                          )}
                        </span>
                      }
                    />
                  );
                })()}
              </div>

              {/* Shipping Information Section */}
              <div className="mt-8">
                <h4 className="text-base lg:text-[20px] font-aero text-white uppercase mb-4">
                  Shipping Information
                </h4>
                <p className="text-white/60 text-sm mb-6">
                  Save your shipping details to automatically fill them when creating shipments.
                </p>
                
                <div className="flex flex-col lg:flex-row gap-5 w-full">
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">Shipping Name *</span>
                    <TextInput
                      placeholder="Your full name for shipping"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => setShippingFormData(prev => ({ ...prev, name: value }))}
                      value={shippingFormData.name || shippingInfo.name || ''}
                    />
                  </label>
                  {(() => {
                    const shippingPhoneValue = shippingFormData.phone || shippingInfo.phone || '';
                    console.log('UserProfile - Shipping phone value:', shippingPhoneValue);
                    console.log('UserProfile - shippingFormData.phone:', shippingFormData.phone);
                    console.log('UserProfile - shippingInfo.phone:', shippingInfo.phone);
                    
                    return (
                      <PhoneInput
                        placeholder="Phone number for shipping"
                        inputClassName="placeholder:text-white/60"
                        onChange={(value) => setShippingFormData(prev => ({ ...prev, phone: value }))}
                        defaultValue={shippingPhoneValue}
                        label="Shipping Phone *"
                        required={true}
                      />
                    );
                  })()}
                </div>

                <div className="flex flex-col lg:flex-row gap-5 w-full mt-5">
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">Street Address *</span>
                    <TextInput
                      placeholder="Street address"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => setShippingFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, street: value }
                      }))}
                      value={shippingFormData.address?.street || shippingInfo.address?.street || ''}
                    />
                  </label>
                </div>

                <div className="flex flex-col lg:flex-row gap-5 w-full mt-5">
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">City *</span>
                    <TextInput
                      placeholder="City"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => setShippingFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, city: value }
                      }))}
                      value={shippingFormData.address?.city || shippingInfo.address?.city || ''}
                    />
                  </label>
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">State *</span>
                    <TextInput
                      placeholder="State"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => setShippingFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, state: value }
                      }))}
                      value={shippingFormData.address?.state || shippingInfo.address?.state || ''}
                    />
                  </label>
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">ZIP Code *</span>
                    <TextInput
                      placeholder="ZIP Code"
                      inputClassName="placeholder:text-white/60"
                      onChange={(value) => setShippingFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, postalCode: value }
                      }))}
                      value={shippingFormData.address?.postalCode || shippingInfo.address?.postalCode || ''}
                    />
                  </label>
                </div>

                <div className="flex flex-col lg:flex-row gap-5 w-full mt-5">
                  <label className="w-full text-white">
                    <span className="text-sm mb-3 block">Country</span>
                    <select
                      value={shippingFormData.address?.country || shippingInfo.address?.country || 'US'}
                      onChange={(e) => setShippingFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, country: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder:text-white/60"
                      style={{
                        backgroundColor: '#1f2937',
                        color: 'white'
                      }}
                    >
                      <option value="US" style={{ backgroundColor: '#1f2937', color: 'white' }}>United States</option>
                      <option value="CA" style={{ backgroundColor: '#1f2937', color: 'white' }}>Canada</option>
                      <option value="MX" style={{ backgroundColor: '#1f2937', color: 'white' }}>Mexico</option>
                      <option value="UK" style={{ backgroundColor: '#1f2937', color: 'white' }}>United Kingdom</option>
                      <option value="DE" style={{ backgroundColor: '#1f2937', color: 'white' }}>Germany</option>
                      <option value="FR" style={{ backgroundColor: '#1f2937', color: 'white' }}>France</option>
                      <option value="JP" style={{ backgroundColor: '#1f2937', color: 'white' }}>Japan</option>
                      <option value="AU" style={{ backgroundColor: '#1f2937', color: 'white' }}>Australia</option>
                    </select>
                  </label>
                </div>
              </div>

              <hr className="border-gray mt-10 mb-6" />

              <ProfileButton
                divClassName="w-full lg:max-w-[250px] bg-primary"
                onClick={onSubmitUserProfile}
                buttonClassName="text-sm group-hover:bg-white group-hover:text-black"
              >
                Save Profile
              </ProfileButton>
            </div>
          </div>

          <div className="flex justify-between w-full mb-5  mt-[80px]">
            <span className="block uppercase text-primary text-[12px] max-lg:mb-8">
              Membership Info
            </span>
            {/* <button className="text-white text-[12px] underline z-[10]" onClick={() => setShowDialogMembership(true)}>
              Select a plan
            </button> */}
          </div>

          <div className="lg:w-[830px] ml-auto relative z-[1]">
            <Slider
              {...sliderSettings}
              className="w-full  membership-slider  dots-down-membership"
            >
              <button
                onClick={() => setActiveTab("basic")}
                className="relative"
              >
                <div
                  className={`px-5 py-9 min-h-[353px] bg-[#0d0d0d] text-white ${
                    initialData.membership === "basic" || activeTab === "basic"
                      ? "border-style-decoration-active"
                      : "border-style-decoration"
                  }`}
                >
                  <div className="w-fit px-3 py-1 text-[10px] rounded-[12px] text-white bg-primary border-white border-[1px]">
                    Basic Plan
                  </div>

                  <div className="flex items-end mt-6">
                    <h2 className="uppercase font-bold text-3xl">
                      ${membershipsForUser["basic"].price}
                    </h2>
                    {/* <span className="text-1xl font-medium text-white ml-2 mr-3">
                      + ${membershipsForUser["basic"].fee}
                    </span> */}
                    <span className="text-base"> /month</span>
                  </div>
                  <p className="text-sm text-white/60 mt-3">{annualSavingsText("basic")}</p>

                  <div className="flex gap-y-4 flex-col mt-10">
                    {membershipsForUser["basic"].benefits.map((item, index) => (
                      <div className="w-full flex" key={`${item}-${index}`}>
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-full mr-2 max-w-[18px]"
                        />
                        <span className="text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {initialData.membership === "basic" && (
                  <div className="absolute !h-fit top-[-5px] lg:right-[-10px] right-0 z-[100] text-xs bg-white border-[1px] border-primary px-5 py-2 text-black rotate-[8deg]">
                    Current Plan
                  </div>
                )}

                {activeTab === "basic" &&
                  initialData.membership !== "basic" && (
                    <>
                      <button
                        className={`border-[1px] text-white py-3 w-full mt-4 text-xs border-primary hover:text-primary hover:bg-white transition`}
                        onClick={() => openPlanDialog("basic")}
                      >
                        {initialData.membership === "free"
                          ? "Select Plan"
                          : "Change Plan"}
                      </button>
                    </>
                  )}
              </button>

              <button onClick={() => setActiveTab("pro")} className="relative">
                <div
                  className={`px-5 py-9 min-h-[353px] bg-[#0d0d0d] text-white ${
                    initialData.membership === "pro" || activeTab === "pro"
                      ? "border-style-decoration-active"
                      : "border-style-decoration"
                  }`}
                >
                  <div className="w-fit px-3 py-1 text-[10px] rounded-[12px] text-white bg-primary border-white border-[1px]">
                    Pro Plan
                  </div>

                  <div className="flex items-end mt-6">
                    <h2 className="uppercase font-bold text-3xl">
                      ${membershipsForUser["pro"].price}
                    </h2>
                    {/* <span className="text-1xl font-medium text-white ml-2 mr-3">
                      + ${membershipsForUser["pro"].fee}
                    </span> */}
                    <span className="text-base"> /month</span>
                  </div>
                  <p className="text-sm text-white/60 mt-3">{annualSavingsText("pro")}</p>

                  <div className="flex gap-y-4 flex-col mt-10">
                    {membershipsForUser["pro"].benefits.map((item, index) => (
                      <div className="w-full flex" key={`${item}-${index}`}>
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-full mr-2 max-w-[18px]"
                        />
                        <span className="text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {initialData.membership === "pro" && (
                  <div className="absolute !h-fit top-[-5px] lg:right-[-10px] right-0 z-[100] text-xs bg-white border-[1px] border-primary px-5 py-2 text-black rotate-[8deg]">
                    Current Plan
                  </div>
                )}

                {activeTab === "pro" && initialData.membership !== "pro" && (
                  <>
                    <button
                      className={`border-[1px] text-white py-3 w-full mt-4 text-xs border-primary hover:text-primary hover:bg-white transition`}
                      onClick={() => openPlanDialog("pro")}
                    >
                      {initialData.membership === "free"
                        ? "Select Plan"
                        : "Change Plan"}
                    </button>
                  </>
                )}
              </button>

              <button
                onClick={() => setActiveTab("elite")}
                className="relative"
              >
                <div
                  className={`px-5 py-9 min-h-[353px] bg-[#0d0d0d] text-white ${
                    initialData.membership === "elite" || activeTab === "elite"
                      ? "border-style-decoration-active"
                      : "border-style-decoration"
                  }`}
                >
                  <div className="w-fit px-3 py-1 text-[10px] rounded-[12px] text-white bg-primary border-white border-[1px]">
                    Elite Plan
                  </div>

                  <div className="flex items-end mt-6">
                    <h2 className="uppercase font-bold text-3xl">
                      ${membershipsForUser["elite"].price}
                    </h2>
                    {/* <span className="text-1xl font-medium text-white ml-2 mr-3">
                      + ${membershipsForUser["elite"].fee}
                    </span> */}
                    <span className="text-base"> /month</span>
                  </div>
                  <p className="text-sm text-white/60 mt-3">{annualSavingsText("elite")}</p>

                  <div className="flex gap-y-4 flex-col mt-10">
                    {membershipsForUser["elite"].benefits.map((item, index) => (
                      <div className="w-full flex" key={`${item}-${index}`}>
                        <img
                          alt="circle"
                          src="/assets/icons/icon-check-circle.svg"
                          className="w-full mr-2 max-w-[18px]"
                        />
                        <span className="text-xs">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {initialData.membership === "elite" && (
                  <div className="absolute !h-fit top-[-5px] lg:right-[-10px] right-0 z-[100] text-xs bg-white border-[1px] border-primary px-5 py-2 text-black rotate-[8deg]">
                    Current Plan
                  </div>
                )}

                {activeTab === "elite" &&
                  initialData.membership !== "elite" && (
                    <>
                      <button
                        className={`border-[1px] text-white py-3 w-full mt-4 text-xs border-primary hover:text-primary hover:bg-white transition`}
                        onClick={() => openPlanDialog("elite")}
                      >
                        {initialData.membership === "free"
                          ? "Select Plan"
                          : "Change Plan"}
                      </button>
                    </>
                  )}
              </button>
            </Slider>
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfile;
